import {
  useListClaimsByOrganizationSitesInRange,
  useGetSiteMetrics,
  useListStatus,
  useListSitesByUser,
} from 'api/resource';
import { useCombineStates } from 'hooks/useCombineStates';
import { useMemo, useState } from 'react';
import { useCurrentSiteStore } from 'store/SiteStore';
import { useCurrentUserStore } from 'store/UserStore';

import {
  getCurrentMonthRange,
  toDateMillis,
  parseDateTime,
  toISODateTime,
} from 'utils/dateUtils';

export const useDashboardData = () => {
  const { organization, id: userID } = useCurrentUserStore();
  const { id: siteID, timezone } = useCurrentSiteStore();
  const { data: sites = [] } = useListSitesByUser({
    userID,
  });
  const siteIDs = sites.map((site) => site.id);
  const statuses = useListStatus({});

  const [startDate, setStartDate] = useState<string>(() =>
    toISODateTime(
      parseDateTime(getCurrentMonthRange(timezone)['0'], {
        zone: timezone,
      }).startOf('day')
    )
  );

  const [endDate, setEndDate] = useState<string>(() =>
    toISODateTime(
      parseDateTime(getCurrentMonthRange(timezone)['1'], {
        zone: timezone,
      }).endOf('day')
    )
  );
  const claimsByOrganization = useListClaimsByOrganizationSitesInRange({
    sites: siteIDs,
    startDate: startDate,
    endDate: endDate,
  });

  const siteMetrics = useGetSiteMetrics(siteID);

  const { isLoading } = useCombineStates([
    siteMetrics,
    claimsByOrganization,
    statuses,
  ]);

  const metricData = useMemo(() => {
    const siteMetricsData = siteMetrics.data;
    if (!siteMetricsData) {
      return {
        staleClaims: 0,
        staleProviders: [],
        errorClaims: 0,
        errorProviders: [],
        latestSubmission: null,
        latestRemittance: null,
      };
    }
    return {
      agingClaims: Number(siteMetricsData.agingClaims) || 0,
      agingClaimProviders: siteMetricsData.agingClaimProviders || [],
      errorClaims: Number(siteMetricsData.errorClaims) || 0,
      errorClaimProviders: siteMetricsData.errorClaimProviders || [],
      latestRemittance: siteMetricsData.latestRemittance,
      latestSubmission: siteMetricsData.latestSubmission,
    };
  }, [siteMetrics.data]);

  const chartData = useMemo(() => {
    if (!claimsByOrganization.data) return [];

    const dateKeys = new Set<string>();
    const processedData: Array<{ [key: string]: number | string }> = [];

    // Collect all unique dates across all sites
    siteIDs.forEach((siteID) => {
      const siteData = claimsByOrganization.data[siteID];
      if (siteData) {
        Object.keys(siteData).forEach((date) => dateKeys.add(date));
      }
    });

    // Sort the date keys
    const sortedDateKeys = Array.from(dateKeys).sort(
      (a, b) => toDateMillis(a) - toDateMillis(b)
    );

    // Populate the chart data by date and site
    sortedDateKeys.forEach((date) => {
      const dateEntry: { [key: string]: number | string } = { date };
      siteIDs.forEach((siteID) => {
        const siteIndex = sites.findIndex((site) => site.id === siteID);
        const siteName = sites[siteIndex]?.name || `Site ${siteIndex + 1}`;
        dateEntry[siteName] = claimsByOrganization.data[siteID]?.[date] || 0;
      });
      processedData.push(dateEntry);
    });

    return processedData;
  }, [claimsByOrganization.data, siteIDs, sites]);

  return {
    metricData,
    statuses: statuses.data || [],
    chartData,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    isLoading,
    organization,
    loadingChart: claimsByOrganization.isLoading,
  };
};
